import React, { useMemo, useState } from "react";
import {
  Menu,
  X,
  ChevronDown,
  Mail,
  Linkedin,
  Facebook,
  Twitter,
  Youtube,
  ArrowRight,
  Users,
  Shield,
  Brain,
  AlertTriangle,
  Lock,
  Database,
  Scale,
  Globe,
  Calendar,
  FileText,
  CheckCircle,
} from "lucide-react";
import { IOrganizationProps } from "../../../../types/Organization";
import { IEventProps } from "../../../../types/Event";
import { usePerformRequest } from "../../../../lib/usePerformRequest";
import { Endpoints } from "../../../../lib/endpoints";
import { Loader } from "../../../../components/ui/loader";
import "./styles.scss";

interface HomepageThemeFiveProps {
  organizationId: number;
  primaryColor?: string;
  secondaryColor?: string;
}

const HomepageThemeFive: React.FC<HomepageThemeFiveProps> = ({
  organizationId,
  primaryColor = "#1a1a1a",
  secondaryColor = "#ffffff",
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  // Fetch organization data
  const organizationEndpoint = useMemo(() => {
    return Endpoints.GetSingleOrganization(organizationId);
  }, [organizationId]);

  const { data: orgResponse, isLoading: isLoadingOrg } =
    usePerformRequest<IOrganizationProps>({
      method: organizationEndpoint.method as any,
      route: organizationEndpoint.route,
    });

  const organization = useMemo(() => {
    if (!orgResponse) return null;
    const data = (orgResponse as any)?.data || orgResponse;
    return data as IOrganizationProps;
  }, [orgResponse]);

  // Fetch events
  const eventsEndpoint = useMemo(() => {
    return Endpoints.GetOrganizationEvents(organizationId);
  }, [organizationId]);

  const { data: eventsResponse, isLoading: isLoadingEvents } =
    usePerformRequest<IEventProps[]>({
      method: eventsEndpoint.method as any,
      route: eventsEndpoint.route,
    });

  const events = useMemo(() => {
    if (!eventsResponse) return [];
    const data = (eventsResponse as any)?.data || eventsResponse;
    return Array.isArray(data) ? data : [];
  }, [eventsResponse]);

  // Focused Domains data
  const domains = [
    {
      title: "People & Culture",
      description:
        "Strengthening leadership, ethics, and culture in a digital world.",
      icon: Users,
    },
    {
      title: "Board & Executive Responsibility",
      description:
        "Supporting Boards and leaders in their oversight, fiduciary, and governance responsibilities.",
      icon: Shield,
    },
    {
      title: "Emerging Technologies",
      description:
        "Governing the adoption and risk of AI, quantum computing, and other frontier technologies.",
      icon: Brain,
    },
    {
      title: "Risk & Resilience",
      description:
        "Integrating risk management with business continuity and enterprise resilience.",
      icon: AlertTriangle,
    },
    {
      title: "Privacy & Ethics",
      description:
        "Upholding data rights, ethical practices, and public confidence.",
      icon: Lock,
    },
    {
      title: "Cybersecurity",
      description:
        "Defending against threats, building resilience, and protecting ecosystems of trust.",
      icon: Shield,
    },
    {
      title: "Data Integrity & Governance",
      description:
        "Ensuring trusted, high-quality intelligent data as the foundation of decisions and Intelligence; both human and AI",
      icon: Database,
    },
    {
      title: "Legal & Regulatory",
      description:
        "Navigating evolving regulations, disclosure requirements, and compliance expectations.",
      icon: Scale,
    },
  ];

  // Testimonials
  const testimonials = [
    {
      quote:
        '"Finally, a forum that connects all the voices shaping digital trust - privacy, security, legal, and resilience. TheDRA is where the future will be governed."',
      author: "– Founding Member",
    },
    {
      quote:
        "\"Boards have been waiting for this for the past few years. TheDRA helps us leaders understand not only today's risks, but tomorrow's.\"",
      author: "– Board Director",
    },
    {
      quote:
        '"This is the missing village. We have needed these conversations to bridge silos and strengthen our defenses."',
      author: "– Industry Practitioner",
    },
  ];

  const handleDiscoverMore = () => {
    const aboutSection = document.getElementById("about-section");
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (isLoadingOrg || isLoadingEvents) {
    return (
      <div className="thedra-loading">
        <Loader size="lg" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="thedra-error">
        <p>Organization not found</p>
      </div>
    );
  }

  return (
    <div
      className="thedra-theme"
      style={
        {
          "--primary-color": primaryColor,
          "--secondary-color": secondaryColor,
        } as React.CSSProperties
      }
    >
      {/* Header */}
      <header className="thedra-header">
        <div className="thedra-container">
          <div className="thedra-header-content">
            <div className="thedra-logo">
              {organization.logo ? (
                <img src={organization.logo} alt={organization.name} />
              ) : (
                <span>{organization.name}</span>
              )}
            </div>

            <nav className={`thedra-nav ${isMenuOpen ? "open" : ""}`}>
              <a href="#home">Home</a>
              <a href="#about">About Us</a>
            </nav>

            <div className="thedra-header-actions">
              <button className="thedra-btn-login">
                <Users size={18} />
                Log In
              </button>
              <button
                className="thedra-menu-toggle"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="thedra-hero">
        <div className="thedra-hero-background">
          <div className="thedra-hero-overlay"></div>
        </div>
        <div className="thedra-container">
          <div className="thedra-hero-content">
            <div className="thedra-hero-logo">
              <svg
                preserveAspectRatio="xMidYMid meet"
                data-bbox="0 0 553.462 213.095"
                viewBox="0 0 553.462 213.095"
                xmlns="http://www.w3.org/2000/svg"
                data-type="color"
                role="presentation"
                aria-hidden="true"
                aria-label=""
              >
                <g>
                  <path
                    d="M11.848 119.235H0v48.527h11.848V149.93h46.554v-12.876H11.847z"
                    fill="#0072f5"
                    data-color="1"
                  ></path>
                  <path
                    d="M0 72.718h23.104v23.757H0v12.89h58.402v-12.89h-23.45V72.718h23.45v-12.89H0z"
                    fill="#0072f5"
                    data-color="1"
                  ></path>
                  <path
                    d="M46.972 32.977H34.707V5.361h-11.43v27.616H11.43V1.604H0v44.177h58.402V1.185h-11.43z"
                    fill="#0072f5"
                    data-color="1"
                  ></path>
                  <path
                    d="M478.629 0H444.93l-1.066 2.515 17.324 41.792.115-.29 22.08 54.019h-.056l19.486 48.05 8.806 21.676h38.209z"
                    fill="#0072f5"
                    data-color="1"
                  ></path>
                  <path
                    d="M292.369 82.096v-.072h-88.136v9.697c-2.854 25.78-22.138 42.962-49.696 42.962h-28.25V34.263h28.25c19.86 0 35.413 9.133 43.643 23.917h40.717c-10.666-33.628-42.26-56.995-84.36-56.995H89.75v166.577h64.787c43.917 0 76.404-25.752 85.628-61.822h15.955v61.822h36.552v-53.296h.115V82.111h-.403z"
                    fill="#06065a"
                    data-color="2"
                  ></path>
                  <path
                    d="m433.026 28.064-49.321 116.23-24.575-36.013c21.13-7.847 35.6-24.754 35.6-51.633v-.477c0-15.708-4.986-28.786-14.24-38.078-10.91-10.954-27.298-16.894-48.413-16.894h-75.942v56.995h36.55V34.278h36.308c17.8 0 28.71 8.092 28.71 23.8v.478c0 14.046-10.204 23.555-28.004 23.555h-28.956l39.016 59.87 17.238 25.795h54.006l9.916-24.379 18.362-45.332h-.043l4.425-10.838 6.5-15.911-17.151-43.223z"
                    fill="#06065a"
                    data-color="2"
                  ></path>
                  <path
                    d="M10.824 184.771H0v27.833h10.824c8.72 0 14.76-6.084 14.76-13.917v-.087c0-7.832-6.025-13.844-14.76-13.844zm8.374 14.003c0 4.928-3.373 8.31-8.374 8.31H6.111v-16.778h4.713c5.001 0 8.374 3.453 8.374 8.396v.086z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M40.386 184.771h-6.112v27.833h6.111z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M63.475 201.881h6.154v3.974c-1.542 1.113-3.53 1.676-5.75 1.676-4.915 0-8.403-3.743-8.403-8.829v-.087c0-4.726 3.531-8.67 7.97-8.67 3.214 0 5.117 1.04 7.264 2.817l3.849-4.653c-2.897-2.47-5.91-3.815-10.911-3.815-8.403 0-14.557 6.488-14.557 14.393v.087c0 8.237 5.952 14.321 14.6 14.321 5.074 0 9.008-1.994 11.862-4.408v-12.095H63.49v5.29z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M90.773 184.771h-6.11v27.833h6.11z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M98.715 190.421h8.446v22.197h6.11v-22.197h8.447v-5.65H98.715z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="m135.339 184.57-11.905 28.034h6.226l2.537-6.243h11.747l2.536 6.243h6.385L140.96 184.57h-5.635zm-.951 16.386 3.69-9.032 3.689 9.032z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M166.24 184.771h-6.096v27.847h19.948v-5.578H166.24z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M225.723 194.034v-.072c0-2.63-.836-4.813-2.378-6.359-1.83-1.835-4.554-2.818-8.086-2.818H202.56v27.833h6.112v-8.902h4.799l5.953 8.902h7.134l-6.788-9.942c3.53-1.315 5.952-4.133 5.952-8.628zm-6.197.318c0 2.341-1.701 3.945-4.685 3.945h-6.183v-7.99h6.068c2.969 0 4.8 1.357 4.8 3.973z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M240.597 184.771h-6.111v27.833h6.11z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M261.785 195.956c-4.8-1.228-5.996-1.835-5.996-3.656v-.087c0-1.358 1.225-2.428 3.574-2.428s4.757 1.026 7.22 2.746l3.172-4.61c-2.81-2.269-6.27-3.54-10.32-3.54-5.678 0-9.714 3.338-9.714 8.396v.086c0 5.535 3.603 7.081 9.196 8.512 4.64 1.2 5.592 1.994 5.592 3.541v.086c0 1.633-1.514 2.63-4.007 2.63-3.171 0-5.794-1.315-8.288-3.38l-3.603 4.334c3.33 2.977 7.58 4.451 11.776 4.451 5.996 0 10.19-3.107 10.19-8.627v-.087c0-4.855-3.171-6.879-8.807-8.353z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M303.64 184.771h-7.38l-11.256 12.168v-12.168h-6.111v27.847h6.11v-8.512l3.215-3.338 8.605 11.85h7.336l-11.82-16.026z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="m335.234 184.57-11.906 28.034h6.227l2.537-6.243h11.746l2.537 6.243h6.385l-11.905-28.035h-5.636zm-.952 16.386 3.69-9.032 3.69 9.032z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M366.135 184.771h-6.097v27.847h19.948v-5.578h-13.851z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M393.678 184.771h-6.111v27.847h19.962v-5.578h-13.851z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M421.496 184.771h-6.111v27.833h6.11z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="m440.939 184.57-11.905 28.034h6.226l2.537-6.243h11.746l2.537 6.243h6.385L446.56 184.57h-5.636zm-.951 16.386 3.689-9.032 3.69 9.032z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="m484.38 201.91-13.015-17.139h-5.636v27.847h6.039v-17.703l13.448 17.703h5.189v-27.847h-6.025z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M513.264 207.445c-4.757 0-8.057-3.975-8.057-8.743v-.087c0-4.769 3.372-8.67 8.057-8.67 2.78 0 4.958 1.2 7.105 3.135l3.892-4.494c-2.58-2.543-5.708-4.292-10.954-4.292-8.533 0-14.485 6.488-14.485 14.393v.087c0 7.991 6.068 14.321 14.24 14.321 5.36 0 8.532-1.908 11.386-4.97l-3.891-3.946c-2.177 1.994-4.123 3.266-7.293 3.266"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                  <path
                    d="M538.386 207.17v-5.853h13.087v-5.448h-13.087v-5.65h14.874v-5.448h-20.942v27.847h21.144v-5.448z"
                    fill="#1f1b56"
                    data-color="3"
                  ></path>
                </g>
              </svg>
            </div>
            <div className="thedra-hero-text-overlay">
              <p className="thedra-hero-description">
                Welcome to The Digital Risk Alliance (TheDRA), a global
                member-based nonprofit organization where board members,
                business leaders, regulators, and risk-industry partners all
                unite to address challenges in cybersecurity, privacy, data,
                legal, and resilience - while anticipating emerging threats,
                strengthening governance, and advancing digital trust.
              </p>
              <button
                className="thedra-btn-primary"
                onClick={handleDiscoverMore}
              >
                Discover More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about-section" className="thedra-about">
        <div className="thedra-container">
          <h2 className="thedra-section-title">About TheDRA.</h2>
          <p className="thedra-section-subtitle">
            As a member, this is your section.
          </p>

          <div className="thedra-testimonials">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="thedra-testimonial">
                <p className="thedra-testimonial-quote">{testimonial.quote}</p>
                <p className="thedra-testimonial-author">
                  {testimonial.author}
                </p>
              </div>
            ))}
          </div>

          <div className="thedra-about-features">
            <div className="thedra-feature">
              <h3 className="thedra-feature-title">
                Cross-Disciplinary Collaboration
              </h3>
              <p className="thedra-feature-description">
                We bring together experts and those that have
                "been-in-that-seat" across multiple disciplines including -
                legal, risk, data, technology, privacy, and people - because no
                single perspective is enough in today's ever changing complex
                landscape.
              </p>
            </div>

            <div className="thedra-feature">
              <h3 className="thedra-feature-title">
                Board & Executive Engagement
              </h3>
              <h4 className="thedra-feature-years">2011-2014</h4>
              <p className="thedra-feature-description">
                We equip boards and executives with the insight and foresight
                needed to oversee digital risk and make confident, resilient
                decisions.
              </p>
              <p className="thedra-feature-description">
                Join, and surround yourself, with a trusted network of leaders
                who share their experience, challenges, and solutions in a
                confidential, collaborative environment.
              </p>
            </div>

            <div className="thedra-feature">
              <h3 className="thedra-feature-title">Community of Peers</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Email Signup Section */}
      <section className="thedra-signup">
        <div className="thedra-container">
          <h2 className="thedra-signup-title">Join the Alliance Today!</h2>
          <p className="thedra-signup-subtitle">
            Join our email list and get access to specials deals exclusive to
            our subscribers.
          </p>
          <form className="thedra-signup-form">
            <input
              type="email"
              placeholder="Enter your email here"
              className="thedra-signup-input"
            />
            <button type="submit" className="thedra-btn-primary">
              Sign Up
            </button>
          </form>
        </div>
      </section>

      {/* Focused Domains Section */}
      <section className="thedra-domains">
        <div className="thedra-container">
          <h2 className="thedra-section-title">Focused Domains</h2>
          <p className="thedra-section-subtitle">
            Our work is organized into eight interconnected domains. Together,
            they represent the foundation of digital trust and resilience.
          </p>

          <div className="thedra-domains-grid">
            {domains.map((domain, index) => {
              const IconComponent = domain.icon;
              return (
                <div key={index} className="thedra-domain-card">
                  <h3 className="thedra-domain-title">{domain.title}</h3>
                  <h3 className="thedra-domain-title-sub">{domain.title}</h3>
                  <p className="thedra-domain-description">
                    {domain.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advisory Team Section */}
      <section className="thedra-team">
        <div className="thedra-container">
          <h2 className="thedra-section-title">The Advisory Team</h2>
          <p className="thedra-team-intro">Dedication. Expertise. Passion.</p>
          <p className="thedra-team-description">
            We are a team of seasoned leaders who have each been in that seat.
            Collectively, we've served as executives, operators, and
            practitioners across every domain TheDRA represents. What unites us
            is not only our experience, but also our passion for advancing this
            vital conversation.
          </p>
          <p className="thedra-team-description">
            We've sat on both sides of the table, as decision-makers, advisors,
            and problem solvers, and we understand what's at stake. Each of us
            brings a unique perspective shaped by navigating the complexities of
            digital risk in real-world settings.
          </p>
          <p className="thedra-team-description">
            We believe in solutions that go beyond theory and deliver impact in
            practice. That's why we're here: to engage openly, share what we've
            learned, and partner with you in building the digital and
            operational resilience today's environment demands.
          </p>
          <p className="thedra-team-description">
            Together, we're not just experienced, we're committed to helping
            this community thrive.
          </p>

          {/* Team members placeholder */}
          <div className="thedra-team-grid">
            <div className="thedra-team-member">
              <div className="thedra-team-avatar">
                {organization.logo ? (
                  <img src={organization.logo} alt="Team Member" />
                ) : (
                  <Users size={48} />
                )}
              </div>
              <h3 className="thedra-team-name">{organization.name}</h3>
              <p className="thedra-team-role">Organization</p>
              <p className="thedra-team-email">{organization.email}</p>
              <div className="thedra-team-social">
                {organization.linkedin_link && (
                  <a
                    href={organization.linkedin_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Linkedin size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="thedra-events">
        <div className="thedra-container">
          <h2 className="thedra-section-title">Events</h2>
          {events && events.length > 0 ? (
            <div className="thedra-events-grid">
              {events.map((event) => (
                <div key={event.id} className="thedra-event-card">
                  {event.cover_image && (
                    <div className="thedra-event-image">
                      <img src={event.cover_image} alt={event.name} />
                    </div>
                  )}
                  <div className="thedra-event-content">
                    <h3 className="thedra-event-title">{event.name}</h3>
                    <p className="thedra-event-date">
                      {new Date(event.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    {event.event_description && (
                      <p className="thedra-event-description">
                        {event.event_description.substring(0, 150)}...
                      </p>
                    )}
                    <button className="thedra-btn-primary">Get Ticket</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="thedra-no-events">No events at the moment</p>
          )}
        </div>
      </section>

      {/* Insights Section */}
      <section id="insights" className="thedra-insights">
        <div className="thedra-container">
          <h2 className="thedra-section-title">Insights</h2>
          <div className="thedra-insights-grid">
            <div className="thedra-insight-card">
              <h3 className="thedra-insight-title">
                Empowering CISOs: The Digital Risk Alliance Flagship
                OfferingsEmpowering CISOs: The Digital Risk Alliance Flagship
                Offerings
              </h3>
            </div>
            <div className="thedra-insight-card">
              <h3 className="thedra-insight-title">
                Cybersecurity Strategies: TheDRA Peer Benchmarking
                WorkshopsCybersecurity Strategies: TheDRA Peer Benchmarking
                Workshops
              </h3>
            </div>
            <div className="thedra-insight-card">
              <h3 className="thedra-insight-title">
                Building Digital Trust: TheDRA Content & Thought
                LeadershipBuilding Digital Trust: TheDRA Content & Thought
                Leadership
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="thedra-services">
        <div className="thedra-container">
          <h2 className="thedra-section-title">Services</h2>
          <div className="thedra-services-table">
            <div className="thedra-services-header">
              <div className="thedra-service-header-cell">
                Service Information
              </div>
            </div>
            <div className="thedra-service-row">
              <div className="thedra-service-name">
                Risk Analysis & Plan Review
              </div>
              <div className="thedra-service-divider"></div>
              <div className="thedra-service-price">1,250 US dollars</div>
              <div className="thedra-service-price-display">$1,250</div>
              <div className="thedra-service-action">
                <button className="thedra-btn-primary">Book Now</button>
              </div>
            </div>
            <div className="thedra-service-row">
              <div className="thedra-service-name">
                AI & Data Governance Review
              </div>
              <div className="thedra-service-divider"></div>
              <div className="thedra-service-price">1,250 US dollars</div>
              <div className="thedra-service-price-display">$1,250</div>
              <div className="thedra-service-action">
                <button className="thedra-btn-primary">Book Now</button>
              </div>
            </div>
            <div className="thedra-service-row">
              <div className="thedra-service-name">
                Board Program: AI & Digital Risk
              </div>
              <div className="thedra-service-divider"></div>
              <div className="thedra-service-price">3,500 US dollars</div>
              <div className="thedra-service-price-display">$3,500</div>
              <div className="thedra-service-action">
                <button className="thedra-btn-primary">Book Now</button>
              </div>
            </div>
            <div className="thedra-service-row">
              <div className="thedra-service-name">
                Executive Program: AI & Digital Risk
              </div>
              <div className="thedra-service-divider"></div>
              <div className="thedra-service-price">3,500 US dollars</div>
              <div className="thedra-service-price-display">$3,500</div>
              <div className="thedra-service-action">
                <button className="thedra-btn-primary">Book Now</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="thedra-footer">
        <div className="thedra-container">
          <div className="thedra-footer-content">
            <div className="thedra-footer-section">
              <p className="thedra-footer-email">
                {organization.email || "info@TheDRA.org"}
              </p>
            </div>
            <div className="thedra-footer-section">
              <div className="thedra-footer-social">
                {organization.linkedin_link && (
                  <a
                    href={organization.linkedin_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Linkedin size={20} />
                  </a>
                )}
              </div>
            </div>
            <div className="thedra-footer-section">
              <a href="#privacy" className="thedra-footer-link">
                Privacy Policy
              </a>
              <a href="#accessibility" className="thedra-footer-link">
                Accessibility Statement
              </a>
            </div>
            <div className="thedra-footer-section">
              <p className="thedra-footer-copyright">
                © {new Date().getFullYear()} by{" "}
                {organization.name || "The Digital Risk Alliance (TheDRA)"}.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomepageThemeFive;
